import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Container,
  Typography,
  TextField,
  Grid,
  Paper,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Autocomplete,
  CircularProgress,
  AppBar,
  Toolbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import CustomButton from "../../components/Custom/CustomButton";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import api from "../../globals/axios";
import { useNavigate } from "react-router-dom";
import AddressForm from "../../components/Custom/AddressForm";
import FormField from "../../components/Biochar/FormField";
import { personalInfoFields } from "../../Data/fieldData";
import { useAlert } from "../../components/Alert";
import { biocharRequestValidationSchema } from "../../components/Custom/ValidationSchema";
import { isAuthenticated } from "../../globals/authentication";
import FarmappLogo from "../../assets/logo/FarmAppLogo.png";
import { transformBackendErrors } from "../../globals/utils";
import {
  SectionHeader,
  initialBiocharFormData,
  requestDetailFields,
  prepareBiocharSubmissionData,
} from "../../utils/biocharFormUtils";

const RequestBiochar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialBiocharFormData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [farms, setFarms] = useState([]);
  const [farmLoading, setFarmLoading] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const { showAlert } = useAlert();
  const isUserAuthenticated = isAuthenticated();

  useEffect(() => {
    if (!isUserAuthenticated) {
      setFarms([]);
      return;
    }

    const fetchFarms = async () => {
      setFarmLoading(true);
      try {
        const response = await api.get("/farmer/farms/");
        setFarms(response.data || []);
      } catch (error) {
        console.error("Error fetching farms:", error);
      } finally {
        setFarmLoading(false);
      }
    };

    fetchFarms();
  }, [isUserAuthenticated]);

  const handleChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: null,
      }));
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for the field being changed
    if (errors[field]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: null,
      }));
    }
  };

  const updateAddressData = (newData) => {
    setFormData((prev) => ({ ...prev, ...newData }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submissionData = prepareBiocharSubmissionData(
      formData,
      isUserAuthenticated
    );

    try {
      const schema = biocharRequestValidationSchema();
      await schema.validate(submissionData, { abortEarly: false });
      await api.post("/biochar/biochar-interest/", submissionData);
      showAlert(
        "success",
        t("Your biochar request has been submitted successfully!")
      );

      if (isUserAuthenticated) {
        setTimeout(() => {
          navigate("/biochar");
        }, 1500);
      } else {
        setFormData(initialBiocharFormData);
        setSuccessDialogOpen(true);
      }
    } catch (error) {
      console.error(error);
      if (error.name === "ValidationError") {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
        showAlert("error", "Please fix the errors in the form");
      } else {
        const backendErrors = error.response?.data || {};
        const formattedErrors = transformBackendErrors(backendErrors);
        setErrors(formattedErrors);

        showAlert(
          "error",
          error.response?.data?.message ||
            t("Failed to submit request. Please try again.")
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const SimpleHeader = () => {
    return (
      <AppBar
        position="static"
        sx={{ bgcolor: "white", boxShadow: "0px 2px 4px rgba(0,0,0,0.1)" }}
      >
        <Toolbar>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src={FarmappLogo}
                alt="FarmApp Logo"
                style={{ height: "40px" }}
              />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    );
  };

  const handleCreateAnother = () => {
    setSuccessDialogOpen(false);
    setFormData(initialBiocharFormData);
    setErrors({});
  };

  return (
    <>
      <Helmet>
        <title>Request Biochar - Farmapp</title>
      </Helmet>
      {isUserAuthenticated ? <CustomAppBar /> : <SimpleHeader />}

      {/* Success Dialog for unauthenticated users */}
      <Dialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t("Request Submitted Successfully")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t(
              "Your biochar interest form has been submitted successfully. Thank you for your interest!"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={handleCreateAnother} color="primary">
            {t("Create Another Interest Form")}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <Container maxWidth="md">
        <Box sx={{ mt: 4, mb: 5 }}>
          <CustomTitle>
            {t("EBC (Enriched Biochar Compost) Interest Form")}
          </CustomTitle>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            gutterBottom
            sx={{ mb: 3 }}
          />

          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <div>
              <Grid container spacing={3}>
                <SectionHeader title="Personal Information" t={t} />

                {isUserAuthenticated && farms.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      id="farm-select"
                      options={farms}
                      getOptionLabel={(option) => `${option.farm_name}`}
                      loading={farmLoading}
                      onChange={(event, newValue) => {
                        handleChange("farm_id", newValue?.id);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Farm"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {farmLoading ? (
                                  <CircularProgress size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={isUserAuthenticated ? 6 : 12}>
                  <TextField
                    required
                    fullWidth
                    label={t("Name")}
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    variant="outlined"
                    error={Boolean(errors.name)}
                    helperText={errors.name || ""}
                  />
                </Grid>

                {!isUserAuthenticated && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("Organization")}
                      name="organization"
                      type="text"
                      value={formData.organization}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      variant="outlined"
                      error={Boolean(errors.organization)}
                      helperText={errors.organization || ""}
                    />
                  </Grid>
                )}

                {personalInfoFields
                  .filter(
                    (field) =>
                      field.name !== "name" &&
                      field.name !== "farmId" &&
                      (isUserAuthenticated || field.name !== "organization")
                  )
                  .map((field) => (
                    <Grid item xs={12} sm={field.sm} key={field.name}>
                      <TextField
                        required={field.required}
                        fullWidth
                        label={t(field.label)}
                        name={field.name}
                        type={field.type}
                        value={formData[field.name]}
                        onChange={(e) =>
                          handleChange(e.target.name, e.target.value)
                        }
                        variant="outlined"
                        error={Boolean(errors[field.name])}
                        helperText={errors[field.name] || ""}
                        inputProps={
                          field.maxLength ? { maxLength: field.maxLength } : {}
                        }
                      />
                    </Grid>
                  ))}

                <SectionHeader title="Request Details" t={t} />
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    {t("Delivery Address")}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <AddressForm
                    showAllFields={true}
                    t={t}
                    handleFieldChange={handleFieldChange}
                    profileData={{
                      pincode: formData.pincode,
                      state: formData.state,
                      district: formData.district,
                      postoffice: formData.postoffice,
                      address_line1: formData.address_line1,
                    }}
                    updateProfileData={updateAddressData}
                    fieldKeyMap={{
                      pin_code: "pincode",
                      state: "state",
                      district: "district",
                      post_office: "postoffice",
                      address: "address_line1",
                    }}
                    errors={{
                      pincode: errors.pincode,
                      state: errors.state,
                      district: errors.district,
                      postoffice: errors.postoffice,
                      address_line1: errors.address_line1,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormField
                    name="village"
                    label={t("Village")}
                    value={formData.village}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    error={errors.village}
                    required
                  />
                </Grid>

                {requestDetailFields.map((field) => (
                  <Grid item xs={12} sm={field.sm} key={field.name}>
                    <TextField
                      required={field.required}
                      fullWidth
                      label={t(field.label)}
                      name={field.name}
                      type={field.type}
                      value={formData[field.name]}
                      onChange={(e) => {
                        handleChange(e.target.name, e.target.value);
                      }}
                      variant="outlined"
                      error={Boolean(errors[field.name])}
                      helperText={errors[field.name] || ""}
                    />
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      {t("Preferred Mode of Delivery")}
                    </FormLabel>
                    <RadioGroup
                      row
                      name="preferred_mode_of_delivery"
                      value={formData.preferred_mode_of_delivery}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                    >
                      <FormControlLabel
                        value="SELFPICKUP"
                        control={<Radio />}
                        label={t("Self Pickup")}
                      />
                      <FormControlLabel
                        value="DELIVERY"
                        control={<Radio />}
                        label={t("Delivery to Address")}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <CustomButton
                    onClick={handleSubmit}
                    fullWidth
                    disabled={loading}
                    label={loading ? "Submitting......" : "Submit"}
                  >
                    {t("Submit")}
                  </CustomButton>
                </Grid>
              </Grid>
            </div>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default RequestBiochar;
